import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppContext, 
  SET_ZONE, 
  SET_STYLE, 
  SET_SUNLIGHT, 
  SET_WINDOWS, 
  SET_FACADE, 
  SET_HOUSE_SIZE, 
  SET_SHAPE, 
  SET_BED_SIZE, 
  SET_BED_CONFIG, 
  SET_DESIGN_NAME,
  SET_CURRENT_DESIGN
} from '../context/AppContext';
import { restoreStateFromUrl, URL_STATE_KEYS } from '../utils/urlState';

const UrlStateManager = () => {
  const { state, dispatch } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialized, setIsInitialized] = useState(false);
  const lastStateRef = useRef(null);

    // Restore state from URL parameters on mount
  useEffect(() => {
    const urlParams = {};
    for (const [key, value] of searchParams.entries()) {
      urlParams[key] = value;
    }
    
    console.log('UrlStateManager: Initial URL params:', urlParams);
    
    if (Object.keys(urlParams).length > 0) {
      restoreStateFromUrl(urlParams, dispatch, {
        SET_ZONE,
        SET_STYLE,
        SET_SUNLIGHT,
        SET_WINDOWS,
        SET_FACADE,
        SET_HOUSE_SIZE,
        SET_SHAPE,
        SET_BED_SIZE,
        SET_BED_CONFIG,
        SET_DESIGN_NAME
      });
    }
    
    // Mark as initialized after restoration attempt
    // Add a small delay to ensure state is properly set
    setTimeout(() => {
      console.log('UrlStateManager: Marking as initialized');
      setIsInitialized(true);
    }, 100);
  }, []);

  // Sync state changes to URL parameters - only when state actually changes
  useEffect(() => {
    // Don't sync until after initial restoration is complete
    if (!isInitialized) {
      console.log('UrlStateManager: Not initialized yet, skipping sync');
      return;
    }
    
    // Create a string representation of the current state for comparison
    const currentStateString = JSON.stringify({
      currentDesign: state.currentDesign,
      userId: state.userId
    });
    
    // Only proceed if state has actually changed
    if (lastStateRef.current === currentStateString) {
      console.log('UrlStateManager: State unchanged, skipping sync');
      return;
    }
    
    // Check if this is the first run after initialization (initial state restoration)
    if (lastStateRef.current === null) {
      console.log('UrlStateManager: First run after initialization, setting initial state');
      lastStateRef.current = currentStateString;
      return; // Don't update URL on first run
    }
    
    console.log('UrlStateManager: State changed, syncing to URL');
    lastStateRef.current = currentStateString;
    
    const urlParams = {};
    
    if (state.currentDesign) {
      const design = state.currentDesign;
      
      if (design.zone) urlParams[URL_STATE_KEYS.ZONE] = design.zone;
      if (design.style) urlParams[URL_STATE_KEYS.STYLE] = design.style;
      if (design.sunlight) urlParams[URL_STATE_KEYS.SUNLIGHT] = design.sunlight;
      if (design.windows) urlParams[URL_STATE_KEYS.WINDOWS] = design.windows;
      if (design.facade) urlParams[URL_STATE_KEYS.FACADE] = design.facade;
      if (design.houseSize) urlParams[URL_STATE_KEYS.HOUSE_SIZE] = design.houseSize.toString();
      if (design.shape) urlParams[URL_STATE_KEYS.SHAPE] = design.shape;
      if (design.bedSize) urlParams[URL_STATE_KEYS.BED_SIZE] = design.bedSize;
      if (design.bedConfig) urlParams[URL_STATE_KEYS.BED_CONFIG] = design.bedConfig.toString();
      if (design.designName) urlParams[URL_STATE_KEYS.DESIGN_NAME] = design.designName;
      if (design.id) urlParams[URL_STATE_KEYS.DESIGN_ID] = design.id;
    }
    
    if (state.userId) urlParams[URL_STATE_KEYS.USER_ID] = state.userId;
    
    // Update URL: MERGE into the existing params rather than rebuilding from
    // scratch. This manager only owns URL_STATE_KEYS — set the ones with values,
    // delete owned keys whose state emptied, and leave every foreign param
    // (e.g. the Bed Adjuster's ?bed=) untouched.
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      Object.values(URL_STATE_KEYS).forEach((key) => {
        const value = urlParams[key];
        if (value !== null && value !== undefined && value !== '') {
          newSearchParams.set(key, value.toString());
        } else {
          newSearchParams.delete(key);
        }
      });
      return newSearchParams;
    });
  }, [state.currentDesign, state.userId, setSearchParams, isInitialized]);

  // This component doesn't render anything
  return null;
};

export default UrlStateManager;
